/**
 * Space Weather Cycle Integration
 * Maps real-time NOAA space weather metrics to cycle rings
 */

import type { CycleRing } from '@/lib/cycleTypes';

/**
 * NOAA Space Weather Metrics
 * Sourced from: https://services.swpc.noaa.gov/products/alerts.json
 */
export interface NoaaSpaceWeatherData {
  // Geomagnetic activity (K-index 0-9 scale)
  kIndex?: number;
  
  // Solar wind parameters
  solarWindSpeedKmS?: number; // Speed in km/s
  solarWindSpeedAvgKmS?: number; // Average speed in km/s
  solarWindDensityCm3?: number; // Density in particles/cm³
  
  // Interplanetary magnetic field (IMF)
  imfBz?: number; // Northward (positive) or southward (negative) component in nT
  
  // Electron flux (high energy, energetic)
  electronFlux?: number; // Electrons > 2 MeV (flux units)
  
  // Proton flux
  protonFlux?: number; // Protons > 10 MeV
  
  // Radiation belt indices
  dstIndex?: number; // Disturbance Storm Time (-/+ nT)
  
  // Alert types/severity
  alertSeverity?: 'watch' | 'warning' | 'alert' | 'none';
  alertDuration?: number; // Hours since alert issued
}

/**
 * Create a geomagnetic activity ring based on K-index
 * K-index ranges 0-9, mapped to 10 stages
 * Colors: green (quiet) → yellow → orange → red (storm)
 */
export function createGeomagnticActivityRing(
  kIndex: number = 0,
  date: Date = new Date()
): CycleRing {
  const normalizedK = Math.min(9, Math.max(0, Math.floor(kIndex)));
  
  return {
    id: 'geomagnetic-activity',
    name: 'Geomagnetic Activity',
    stages: 10,
    labels: ['G0', 'G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8', 'G9'],
    colors: [
      '120 70% 45%', // 0: Green (quiet)
      '100 65% 50%', // 1
      '85 80% 50%',  // 2: Yellow-green
      '60 85% 50%',  // 3: Yellow
      '45 90% 50%',  // 4: Yellow-orange
      '30 90% 55%',  // 5: Orange
      '20 85% 55%',  // 6: Orange-red
      '10 90% 55%',  // 7
      '5 95% 55%',   // 8: Red
      '0 100% 60%',  // 9: Bright red (severe storm)
    ],
    currentStage: normalizedK,
    rotation: 0,
    waveform: 'sine',
    waveformIntervalMultiplier: 3, // Smooth oscillations over ~3 hours
    useCurrentStageAsValue: true,
    skinMode: 'secondary',
  };
}

/**
 * Create a solar wind pressure ring
 * Speed ranges: quiet <300, normal 300-400, fast 400-600, extreme >600 km/s
 * Mapped to 5 stages with triangle waveform for sudden impulses
 */
export function createSolarWindRing(
  speedKmS: number = 400
): CycleRing {
  let stage = 0;
  if (speedKmS < 300) stage = 0; // Quiet
  else if (speedKmS < 350) stage = 1; // Slow
  else if (speedKmS < 450) stage = 2; // Normal
  else if (speedKmS < 600) stage = 3; // Fast
  else stage = 4; // Extreme
  
  return {
    id: 'solar-wind-pressure',
    name: 'Solar Wind Pressure',
    stages: 5,
    labels: ['Quiet', 'Slow', 'Normal', 'Fast', 'Extreme'],
    colors: ['120 60% 50%', '100 70% 55%', '60 80% 55%', '30 85% 55%', '0 90% 60%'],
    currentStage: stage,
    rotation: 0,
    waveform: 'triangle',
    waveformIntervalDivider: 2, // Response to shorter-scale variations
    useCurrentStageAsValue: true,
    skinMode: 'secondary',
  };
}

/**
 * Create a radiation belt electron flux ring
 * Tracks high-energy electron population
 * 4 stages: quiet, moderate, high, extreme
 */
export function createElectronFluxRing(
  electronFlux: number = 0
): CycleRing {
  let stage = 0;
  if (electronFlux < 1000) stage = 0; // Quiet
  else if (electronFlux < 10000) stage = 1; // Moderate
  else if (electronFlux < 100000) stage = 2; // High
  else stage = 3; // Extreme
  
  return {
    id: 'electron-flux',
    name: 'Electron Flux',
    stages: 4,
    labels: ['Quiet', 'Moderate', 'High', 'Extreme'],
    colors: ['120 60% 50%', '60 75% 55%', '30 80% 55%', '0 85% 60%'],
    currentStage: stage,
    rotation: 0,
    waveform: 'default',
    useCurrentStageAsValue: true,
    skinMode: 'tertiary',
  };
}

/**
 * Create a space weather alert severity ring
 * Tracks current alert status: none, watch, warning, alert
 * Shows alert age/duration
 */
export function createAlertSeverityRing(
  severity: 'watch' | 'warning' | 'alert' | 'none' = 'none',
  durationHours: number = 0
): CycleRing {
  const severityMap = { none: 0, watch: 1, warning: 2, alert: 3 };
  const stage = severityMap[severity] || 0;
  
  return {
    id: 'alert-severity',
    name: 'Space Weather Alert',
    stages: 4,
    labels: ['None', 'Watch', 'Warning', 'Alert'],
    colors: ['120 60% 50%', '60 80% 55%', '30 85% 55%', '0 100% 60%'],
    currentStage: stage,
    rotation: 0,
    waveform: 'square', // Sharp transitions for alert states
    useCurrentStageAsValue: true,
    skinMode: 'secondary',
  };
}

/**
 * Create a IMF Bz polarity ring
 * Tracks interplanetary magnetic field southward component
 * Critical for auroral substorms (negative Bz > -5 nT often triggers activity)
 */
export function createImfBzRing(bzNt: number = 0): CycleRing {
  let stage = 2; // Neutral (0)
  
  if (bzNt > 10) stage = 4; // Strongly northward (favorable)
  else if (bzNt > 0) stage = 3; // Northward
  else if (bzNt > -5) stage = 2; // Weakly southward
  else if (bzNt > -10) stage = 1; // Southward (storm-like)
  else stage = 0; // Strongly southward (storm)
  
  return {
    id: 'imf-bz',
    name: 'IMF Bz Polarity',
    stages: 5,
    labels: ['S--', 'S-', 'Neutral', 'N+', 'N++'],
    colors: ['0 85% 55%', '20 80% 55%', '180 60% 55%', '240 70% 55%', '270 65% 55%'],
    currentStage: stage,
    rotation: 0,
    waveform: 'sine',
    waveformIntervalMultiplier: 2, // Oscillates with solar wind changes
    useCurrentStageAsValue: true,
    skinMode: 'tertiary',
  };
}

/**
 * Aggregate all space weather cycles into a cohesive ring set
 */
export function createSpaceWeatherCycles(
  data: NoaaSpaceWeatherData
): CycleRing[] {
  const solarWindSpeed =
    data.solarWindSpeedAvgKmS ?? data.solarWindSpeedKmS ?? 0;

  return [
    { ...createGeomagnticActivityRing(data.kIndex ?? 0), displayOrder: 50 },
    { ...createSolarWindRing(solarWindSpeed), displayOrder: 51 },
    { ...createElectronFluxRing(data.electronFlux ?? 0), displayOrder: 52 },
    { ...createAlertSeverityRing(data.alertSeverity ?? 'none', data.alertDuration), displayOrder: 53 },
    { ...createImfBzRing(data.imfBz ?? 0), displayOrder: 54 },
  ];
}

/**
 * Extract space weather metrics from NOAA alert JSON
 * Multiple alerts may be present; we aggregate the most severe
 */
export function parseNoaaAlerts(alerts: any[]): NoaaSpaceWeatherData {
  const data: NoaaSpaceWeatherData = {
    kIndex: 0,
    solarWindSpeedKmS: undefined,
    electronFlux: 0,
    imfBz: 0,
    alertSeverity: 'none',
  };
  
  if (!Array.isArray(alerts)) return data;
  
  const severityIndex = { none: 0, watch: 1, warning: 2, alert: 3 };
  const solarWindSpeeds: number[] = [];
  
  for (const alert of alerts) {
    // Parse product_id to determine alert type and severity
    const productId = alert.product_id?.toLowerCase() || '';
    
    if (productId.includes('k_index')) {
      // Try to extract K-index from message
      const match = alert.message?.match(/k[\s-]*(?:index[\s-]*)?(\d+)/i);
      if (match) data.kIndex = parseInt(match[1], 10);
    }
    
    if (productId.includes('solar_wind')) {
      const match = alert.message?.match(/(\d+)\s*km\/s/);
      if (match) {
        const speed = parseInt(match[1], 10);
        if (!Number.isNaN(speed)) {
          solarWindSpeeds.push(speed);
          if (!data.solarWindSpeedKmS) {
            data.solarWindSpeedKmS = speed;
          }
        }
      }
    }
    
    if (productId.includes('electron')) {
      const match = alert.message?.match(/(\d+)\s*(?:electrons|flux)/i);
      if (match) data.electronFlux = parseInt(match[1], 10);
    }
    
    if (productId.includes('proton')) {
      const match = alert.message?.match(/(\d+)\s*(?:proton|flux)/i);
      if (match) data.protonFlux = parseInt(match[1], 10);
    }
    
    // Determine alert severity from product_id
    if (productId.includes('alert')) {
      const severity: 'alert' = 'alert';
      if (severityIndex[severity] > severityIndex[data.alertSeverity ?? 'none']) {
        data.alertSeverity = severity;
      }
    } else if (productId.includes('warning')) {
      const severity: 'warning' = 'warning';
      if (severityIndex[severity] > severityIndex[data.alertSeverity ?? 'none']) {
        data.alertSeverity = severity;
      }
    } else if (productId.includes('watch')) {
      const severity: 'watch' = 'watch';
      if (severityIndex[severity] > severityIndex[data.alertSeverity ?? 'none']) {
        data.alertSeverity = severity;
      }
    }
  }

  if (solarWindSpeeds.length > 0) {
    const average =
      solarWindSpeeds.reduce((sum, value) => sum + value, 0) / solarWindSpeeds.length;
    data.solarWindSpeedAvgKmS = Number(average.toFixed(1));
  }

  return data;
}
